/**
 * §6.7 Launch Workflow — the 8-step provisioning journey.
 * Horizontal SVG on desktop, stacked grid detail below.
 */

import React from 'react'
import {
  Wallet, CreditCard, ServerCog, Palette, Globe, ToggleRight, UsersRound, Rocket, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { LAUNCH_STEPS } from '../data'
import { TONE } from '../_shared'

const ICONS = { Wallet, CreditCard, ServerCog, Palette, Globe, ToggleRight, UsersRound, Rocket }

export default function LaunchWorkflow() {
  return (
    <section className="relative py-24 md:py-28">
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Launch Workflow</Eyebrow>
          <SectionHeading align="center"
            title={<>Eight steps.{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">One afternoon.</span></>}
            subtitle="From choosing a plan to going live — every provisioning step is automated. The typical tenant is production-ready in under two hours."
          />
        </div>

        {/* Desktop horizontal flow */}
        <div className="hidden lg:block relative">
          <svg viewBox="0 0 1400 220" className="w-full h-auto">
            <defs>
              <linearGradient id="lw-line" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%"   stopColor="#FFC107" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#22c55e" stopOpacity="0.4" />
              </linearGradient>
            </defs>
            <path id="lw-path" d="M 60 110 L 1340 110" stroke="url(#lw-line)" strokeWidth="2" strokeDasharray="4 8" fill="none" className="animate-brand-dash" />
            <circle r="4" fill="#FFC107">
              <animateMotion dur="12s" repeatCount="indefinite"><mpath href="#lw-path" /></animateMotion>
            </circle>
            {LAUNCH_STEPS.map((n, i) => {
              const x = 90 + (1220 * i) / (LAUNCH_STEPS.length - 1)
              const tone = TONE[n.tone] || TONE.orange
              const Icon = ICONS[n.iconKey] || Sparkles
              return (
                <g key={n.id} transform={`translate(${x} 110)`}>
                  <circle r="28" fill="rgba(10,10,12,0.9)" stroke={tone.hex} strokeOpacity="0.55" strokeWidth="1.4" />
                  <circle r="25" fill={`${tone.hex}12`} />
                  <foreignObject x="-13" y="-13" width="26" height="26">
                    <div className="flex items-center justify-center w-full h-full" style={{ color: tone.hex }}>
                      <Icon strokeWidth={1.8} style={{ width: 13, height: 13 }} />
                    </div>
                  </foreignObject>
                  <text y="52" textAnchor="middle" fontSize="10" fontWeight="600" fill="rgba(255,255,255,0.9)">{n.label}</text>
                  <text y="66" textAnchor="middle" fontSize="8" fontFamily="monospace" fill="rgba(255,255,255,0.35)">{String(i + 1).padStart(2, '0')}</text>
                </g>
              )
            })}
          </svg>
        </div>

        {/* Detail grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {LAUNCH_STEPS.map((n, i) => {
            const tone = TONE[n.tone] || TONE.orange
            const Icon = ICONS[n.iconKey] || Sparkles
            return (
              <div key={n.id} className="relative rounded-xl border border-white/10 bg-white/[0.02] p-4 overflow-hidden">
                <div className="pointer-events-none absolute inset-0 opacity-40"
                  style={{ background: `radial-gradient(80% 40% at 20% 0%, rgba(${tone.rgb},0.10), transparent 70%)` }} />
                <div className="relative flex items-center gap-2 mb-1.5">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon style={{ width: 11, height: 11 }} strokeWidth={2} />
                  </span>
                  <span className="text-[9.5px] font-mono uppercase tracking-wider" style={{ color: tone.hex }}>step {String(i + 1).padStart(2, '0')}</span>
                </div>
                <div className="relative text-[13.5px] font-semibold text-white leading-tight">{n.label}</div>
                <p className="relative text-[12px] text-zinc-400 leading-relaxed mt-1.5">{n.body}</p>
              </div>
            )
          })}
        </div>
      </Container>
    </section>
  )
}
